"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";

const sections = [
  "About Lily Lanka",
  "Scope of This Notice",
  "Personal Data We Collect",
  "How We Use Your Personal Data",
  "Legal Bases for Processing",
  "How We Share Personal Data",
  "International Data Transfers",
  "Data Retention",
  "How We Protect Your Data",
  "Use of Cookies and Tracking Technologies",
  "Artificial Intelligence and Automated Processing",
  "Children's Privacy",
  "Your Rights",
  "Updates to This Privacy Notice",
  "Contact Us",
];

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section");
      const scrollPosition = window.scrollY + 200;

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute("id");

        if (
          scrollPosition >= sectionTop &&
          scrollPosition < sectionTop + sectionHeight
        ) {
          setActiveSection(sectionId || "");
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once on mount

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-950 dark:to-gray-900">
      {/* Header */}
      <div className="relative text-black dark:text-white pt-24 md:pt-28 pb-20 shadow-xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto px-6 text-center"
        >
          <h1 className="text-5xl font-bold mb-4 tracking-tight">
            Privacy Notice & Cookie Statement
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            Last updated: October, 2025
          </p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row gap-10">
        {/* Sidebar Table of Contents */}
        <aside className="md:w-1/4 hidden md:block sticky top-28 self-start bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-5 h-fit">
          <h3 className="text-gray-900 dark:text-white font-semibold mb-3 text-lg">
            On this page
          </h3>
          <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
            {sections.map((title) => (
              <li key={title}>
                <a
                  href={`#${title}`}
                  className={`block py-1 px-3 rounded-lg transition-all duration-300 ${
                    activeSection === title
                      ? "text-purple-700 dark:text-purple-400 font-semibold bg-purple-50 dark:bg-purple-950 border-l-4 border-purple-700"
                      : "hover:text-purple-700 dark:hover:text-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-950/50"
                  }`}
                >
                  {title}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        {/* Main Content */}
        <div className="md:w-3/4 space-y-14">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-10 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
          >
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
              This Privacy Notice & Cookie Statement ("Privacy Notice") explains
              how <strong>LILY LANKA (PVT) LTD</strong> ("Lily Lanka", "we",
              "our", or "us") collects, uses, shares, and protects your personal
              data when you use our Lily Lanka website, mobile apps, and other
              online services (collectively, the "Platform").
            </p>
            <p className="text-gray-700 leading-relaxed mt-4 text-lg">
              By accessing our platform, you acknowledge that you have read and
              understood and agree with this Privacy Notice.
            </p>
          </motion.div>

          {/* About Lily Lanka */}
          <motion.section
            id="About Lily Lanka"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              About Lily Lanka
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              <strong>Lily Lanka (Pvt) Ltd</strong> is a Sri Lankan-based
              company incorporated for the purpose of setting up a digital
              booking platform aimed at facilitating the travelers to connect
              with many travel destinations in Sri Lanka also allowing many
              options for accommodation, comparison of rates, digital Payments
              and booking service with related service providers.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We are committed to protecting your privacy and handling your
              personal data in compliance with the laws of Sri Lanka and
              applicable international data protection standards.
            </p>
          </motion.section>

          {/* Scope of This Notice */}
          <motion.section
            id="Scope of This Notice"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Scope of This Notice
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              This Privacy Notice applies to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>Users and travelers using our website or mobile app,</li>
              <li>
                Accommodation providers and business partners using our partner
                platforms, and
              </li>
              <li>
                Any person whose data is collected through our Platform,
                services, or communications.
              </li>
            </ul>
          </motion.section>

          {/* Personal Data We Collect */}
          <motion.section
            id="Personal Data We Collect"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Personal Data We Collect
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              We collect the following types of personal data depending on how
              you interact with us:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Data you provide to us
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-6">
              <li>
                Name, contact details (email address, phone number), and postal
                address;
              </li>
              <li>Booking and payment details;</li>
              <li>Account login credentials (if you create an account);</li>
              <li>
                Preferences (e.g., accommodation type, travel dates, special
                requests);
              </li>
              <li>Reviews or feedback you submit;</li>
              <li>
                Communication content when you contact our support or business
                teams.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Data we collect automatically
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-6">
              <li>
                Device information (IP address, operating system, browser type,
                etc.);
              </li>
              <li>
                Platform activity, including pages visited and actions taken;
              </li>
              <li>Location data (when permitted by your device settings);</li>
              <li>Cookies and similar tracking data.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Data we receive from third parties
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>
                Accommodation providers or travel partners with whom you have
                made bookings;
              </li>
              <li>
                Payment processors or service providers who handle transactions;
              </li>
              <li>
                Marketing and analytics partners who help improve our services.
              </li>
            </ul>
          </motion.section>

          {/* How We Use Your Personal Data */}
          <motion.section
            id="How We Use Your Personal Data"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How We Use Your Personal Data
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We use your personal data to:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2 mb-4">
              <li>Process and confirm your reservations;</li>
              <li>Provide customer service and support;</li>
              <li>Manage your account and preferences;</li>
              <li>Improve our Platform, services, and user experience;</li>
              <li>Send booking confirmations, updates, and relevant offers;</li>
              <li>
                Conduct analytics, performance monitoring, and fraud prevention;
              </li>
              <li>
                Comply with legal obligations under any applicable Sri Lankan
                law, regulation or direction
              </li>
            </ol>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We will only process your personal data for legitimate business
              purposes, in accordance with the consent you provide and
              applicable legal bases.
            </p>
          </motion.section>

          {/* Legal Bases for Processing */}
          <motion.section
            id="Legal Bases for Processing"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Legal Bases for Processing
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We process your personal data under one or more of the following
              legal grounds:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2">
              <li>
                To perform our contract with you (e.g., completing bookings or
                payments);
              </li>
              <li>To comply with legal obligations;</li>
              <li>
                For our legitimate business interests (e.g., improving services,
                preventing fraud);
              </li>
              <li>Based on your consent (e.g., marketing communications).</li>
            </ul>
          </motion.section>

          {/* How We Share Personal Data */}
          <motion.section
            id="How We Share Personal Data"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How We Share Personal Data
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may share your data only as necessary with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li>
                Accommodation providers and service partners to process your
                reservations;
              </li>
              <li>Payment processors to handle secure transactions;</li>
              <li>
                Technology and support service providers that help us maintain
                the Platform;
              </li>
              <li>
                Marketing and analytics partners, only as permitted by law;
              </li>
              <li>
                Government or regulatory authorities, when required by law.
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              All third parties handling personal data on our behalf are
              required to maintain confidentiality and use the data solely for
              authorised purposes.
            </p>
          </motion.section>

          {/* International Data Transfers */}
          <motion.section
            id="International Data Transfers"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              International Data Transfers
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If your personal data is transferred outside Sri Lanka (for
              example, to cloud servers or global service providers), Lily Lanka
              ensures appropriate safeguards are in place, such as contractual
              protections and compliance with international privacy standards.
            </p>
          </motion.section>

          {/* Data Retention */}
          <motion.section
            id="Data Retention"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Data Retention
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We retain your personal data only for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li>Fulfil the purposes stated in this Privacy Notice;</li>
              <li>Comply with applicable legal or tax obligations;</li>
              <li>Resolve disputes or enforce our agreements.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              After the retention period, your data will be securely deleted or
              anonymised.
            </p>
          </motion.section>

          {/* How We Protect Your Data */}
          <motion.section
            id="How We Protect Your Data"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How We Protect Your Data
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We use appropriate technical and organizational measures to
              protect your data, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li>Data encryption and secure storage;</li>
              <li>Restricted access to authorised personnel only;</li>
              <li>Continuous monitoring for potential security breaches;</li>
              <li>Regular system updates and staff training.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If we become aware of a data breach that may affect your rights or
              privacy, we will notify you and the relevant authorities as
              required by law.
            </p>
          </motion.section>

          {/* Use of Cookies and Tracking Technologies */}
          <motion.section
            id="Use of Cookies and Tracking Technologies"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Use of Cookies and Tracking Technologies
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              What are cookies?
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Cookies are small text files placed on your device when you visit
              our website or use our mobile app. They help us recognise your
              device, store preferences, and improve your browsing experience.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Types of cookies we use
            </h3>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-6">
              <li>
                Essential cookies: Enable basic site functionality such as login
                and navigation.
              </li>
              <li>
                Analytical cookies: Help us understand how users interact with
                our Platform.
              </li>
              <li>
                Marketing cookies: Allow us to provide personalised offers and
                measure campaign effectiveness.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Managing cookies
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You can manage or disable cookies through your browser settings.
              However, disabling essential cookies may affect the proper
              functioning of our Platform.
            </p>
          </motion.section>

          {/* Artificial Intelligence and Automated Processing */}
          <motion.section
            id="Artificial Intelligence and Automated Processing"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Artificial Intelligence and Automated Processing
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Lily Lanka may use artificial intelligence (AI) and automated
              tools to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li>Detect and prevent fraudulent or suspicious activity;</li>
              <li>Provide personalised recommendations;</li>
              <li>Analyse user preferences and improve services.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              These systems are designed to assist our human staff and do not
              make legally binding decisions about you without human oversight.
            </p>
          </motion.section>

          {/* Children's Privacy */}
          <motion.section
            id="Children's Privacy"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Children's Privacy
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Our services are not directed toward children under the age of 18.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If we learn that personal data has been collected from a minor
              without parental consent, we will delete it promptly.
            </p>
          </motion.section>

          {/* Your Rights */}
          <motion.section
            id="Your Rights"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Your Rights
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Under applicable law, you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-2 mb-4">
              <li>Access the personal data we hold about you;</li>
              <li>Request correction or deletion of your data;</li>
              <li>Object to or restrict the processing of your data;</li>
              <li>
                Withdraw your consent at any time (for consent-based
                processing);
              </li>
              <li>Request a copy of your data (data portability).</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              To exercise these rights, please contact us as described below.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may need to verify your identity before fulfilling your
              request.
            </p>
          </motion.section>

          {/* Updates to This Privacy Notice */}
          <motion.section
            id="Updates to This Privacy Notice"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="scroll-mt-28"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Updates to This Privacy Notice
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Lily Lanka may update this Privacy Notice periodically to reflect
              changes in law, technology, or our business operations. Updated
              versions will be posted on our website with the date of revision.
            </p>
          </motion.section>

          {/* Contact Us */}
          <motion.section
            id="Contact Us"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="scroll-mt-28"
          >
            <div className="bg-purple-600 to-blue-600 p-8 rounded-2xl text-white shadow-lg">
              <h2 className="text-3xl font-bold mb-4">Contact Us</h2>
              <p className="text-indigo-100 mb-4">
                For questions or concerns regarding this Privacy Notice:
              </p>
              <p className="font-semibold">LILY LANKA (PVT) LTD</p>
              <p>Email: lilylanka@outlook.com</p>
              <p>Phone: +94 71 688 0278</p>
              <p>Address: 425/B/6/C, Mahawatta Road, Ganemulla.Sri Lanka</p>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
}
